import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { useActiveFamily } from '../context/ActiveFamilyContext';
import { useToast } from '../components/ui/toast/ToastProvider';
import { addAndroidPurchaseListener, isGooglePlayBillingSupported } from '../services/googlePlayBilling';
import { upsertAccountSubscription } from '../services/subscriptions';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Listener global para compras Android (Google Play Billing).
 *
 * Nota: esto aplica un upgrade optimista a Familiar. Para producción, Google exige
 * verificación server-side del purchaseToken para determinar expiración.
 */
export function useGooglePlayBillingListener() {
  const { user } = useAuth();
  const { activeFamily } = useActiveFamily();
  const { showToast } = useToast();

  useEffect(() => {
    if (!isGooglePlayBillingSupported()) return;

    let remove: (() => void) | null = null;
    let cancelled = false;

    (async () => {
      remove = await addAndroidPurchaseListener(async (payload) => {
        if (cancelled) return;

        const productId = String(payload?.productIdentifier || payload?.productId || payload?.subscriptionId || '').trim();
        const purchaseToken = String(payload?.purchaseToken || payload?.token || '').trim();

        if (!productId || !purchaseToken) {
          showToast({
            type: 'error',
            message: 'Compra recibida pero faltan datos para validar (productId/purchaseToken).',
          });
          return;
        }

        try {
          const ownerUserId = activeFamily?.ownerId || user?.uid;
          if (!ownerUserId) return;

          showToast({
            type: 'info',
            message: 'Validando compra con Google Play…',
          });

          const functions = getFunctions();
          const verify = httpsCallable(functions, 'verifyGooglePlaySubscription');
          const res: any = await verify({ productId, purchaseToken });

          const active = Boolean(res?.data?.active);
          if (active) {
            await upsertAccountSubscription({ ownerUserId, plan: 'familiar' });
            try {
              if (activeFamily?.id) {
                await updateDoc(doc(db, 'families', activeFamily.id), {
                  plan: 'family',
                  billingStatus: 'active',
                  updatedAt: serverTimestamp(),
                });
              }
            } catch {
            }

            showToast({
              type: 'success',
              message: 'Suscripción activada.',
            });
          } else {
            showToast({
              type: 'error',
              message: 'La suscripción no está activa (expirada o no válida).',
            });
          }
        } catch (e) {
          showToast({
            type: 'error',
            message: 'No se pudo sincronizar la compra. Intenta de nuevo.',
          });
        }
      });
    })();

    return () => {
      cancelled = true;
      try {
        remove?.();
      } catch {
      }
    };
  }, [activeFamily?.id, activeFamily?.ownerId, showToast, user?.uid]);

  return {
    platform: Capacitor.getPlatform(),
  };
}
